/**
 * The access token lives HERE and nowhere else: a module-scoped variable, i.e.
 * plain JS memory.
 *
 * It is deliberately NOT in `localStorage` or `sessionStorage`. Both are
 * readable by any injected script, and both survive across tabs and reloads —
 * so a single XSS payload would exfiltrate a credential that outlives the page.
 * In memory, a reload discards the token and the session is recovered by the
 * silent refresh instead (the refresh token is an HttpOnly cookie the JS in
 * this file can never read or write).
 *
 * Residual risk, stated honestly: while the tab is open, XSS can still call the
 * API as the user. No token-storage scheme fixes that; only CSP and input
 * hygiene reduce it.
 */

let accessToken: string | null = null;

/** Called when the session dies irrecoverably (reuse detected, refresh failed). */
type SessionEndedListener = (reason: SessionEndReason) => void;

export type SessionEndReason = 'FORCE_REAUTH' | 'REFRESH_FAILED' | 'LOGOUT';

const listeners = new Set<SessionEndedListener>();

export function getAccessToken(): string | null {
  return accessToken;
}

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function clearAccessToken(): void {
  accessToken = null;
}

export function onSessionEnded(listener: SessionEndedListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function notifySessionEnded(reason: SessionEndReason): void {
  clearAccessToken();
  for (const listener of [...listeners]) {
    listener(reason);
  }
}

/** Test seam: reset module state between specs. */
export function __resetTokenStore(): void {
  accessToken = null;
  listeners.clear();
}
